import React, { useRef, useState } from 'react';
import axios from 'axios';

axios.defaults.withCredentials = true;

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.ppt,.pptx';

function authHeaders() {
  try {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export default function QuickPrint() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [duplex, setDuplex] = useState(false);
  const [colorMode, setColorMode] = useState('monochrome');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const onPick = (f) => {
    setMsg('');
    setFile(f || null);
  };

  const onPrint = async () => {
    if (!file || busy) return;
    setBusy(true);
    setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('duplex', duplex ? 'long' : 'off');
      fd.append('colorMode', colorMode);
      const { data } = await axios.post('/api/admin/print-upload', fd, {
        headers: {
          ...authHeaders(),
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000,
      });
      if (data?.printed) {
        const head = `已送印：${data.filename || file.name}`;
        setMsg(data.printHint ? `${head}\n\n${data.printHint}` : head);
      } else {
        setMsg(`檔案已上傳但未自動列印。\n${data?.file || ''}\n${data?.hint || ''}`);
      }
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (e) {
      const err = e?.response?.data;
      if (err?.error === 'unsupported_file_type') {
        setMsg('不支援的檔案格式。');
      } else if (err?.error === 'file_too_large') {
        setMsg('檔案過大（上限 25MB）。');
      } else if (e?.response?.status === 401) {
        setMsg('登入已過期，請重新登入後台。');
      } else {
        setMsg(`列印失敗：${err?.message || e?.message || '未知錯誤'}`);
      }
    } finally {
      setBusy(false);
    }
  };

  const isPdf = file && /\.pdf$/i.test(file.name);

  return (
    <div style={{ maxWidth: 520, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 800 }}>上傳列印</h2>
      <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: 14, lineHeight: 1.5 }}>
        選擇檔案後按「一鍵列印」，會送到與預約牌相同的印表機（後端主機送印）。
      </p>

      <div
        style={{
          border: '2px dashed #d1d5db',
          borderRadius: 12,
          padding: 24,
          textAlign: 'center',
          background: '#fafafa',
          marginBottom: 16,
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          style={{ display: 'none' }}
          onChange={(e) => onPick(e.target.files?.[0] || null)}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          style={{
            padding: '10px 20px',
            borderRadius: 8,
            border: '1px solid #9ca3af',
            background: '#fff',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          選擇檔案
        </button>
        {file ? (
          <div style={{ marginTop: 12, fontSize: 14, color: '#374151' }}>
            {file.name}
            {' '}
            <span style={{ color: '#9ca3af' }}>
              (
              {(file.size / 1024).toFixed(0)}
              {' '}
              KB)
            </span>
          </div>
        ) : (
          <div style={{ marginTop: 12, fontSize: 13, color: '#9ca3af' }}>
            PDF、圖片、Word、Excel、PowerPoint
          </div>
        )}
      </div>

      {isPdf ? (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 14, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={duplex}
            onChange={(e) => setDuplex(e.target.checked)}
          />
          長邊雙面（僅 PDF，需 Sumatra）
        </label>
      ) : null}

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>列印色彩</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', marginBottom: 6 }}>
          <input
            type="radio"
            name="colorMode"
            value="monochrome"
            checked={colorMode === 'monochrome'}
            onChange={() => setColorMode('monochrome')}
          />
          黑白列印
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
          <input
            type="radio"
            name="colorMode"
            value="color"
            checked={colorMode === 'color'}
            onChange={() => setColorMode('color')}
          />
          彩色列印
        </label>
      </div>

      <button
        type="button"
        disabled={!file || busy}
        onClick={onPrint}
        style={{
          width: '100%',
          padding: '12px 20px',
          borderRadius: 8,
          border: 'none',
          background: !file || busy ? '#9ca3af' : '#7b4f2f',
          color: '#fff',
          fontWeight: 700,
          fontSize: 16,
          cursor: !file || busy ? 'not-allowed' : 'pointer',
        }}
      >
        {busy ? '送印中…' : '一鍵列印'}
      </button>

      {msg ? (
        <pre
          style={{
            marginTop: 16,
            padding: 12,
            borderRadius: 8,
            background: '#f3f4f6',
            fontSize: 13,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {msg}
        </pre>
      ) : null}
    </div>
  );
}
